import { useShallow } from "zustand/react/shallow";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { toast } from "sonner";
import { usePlatformStore } from "@platform/store";
import { useDevices } from "../../providers/DevicesProvider";
import { suggestedNonContinuityMic } from "../../utils/suggestedNonContinuityMic";
import { logIPhoneMicAlertEvent } from "../../hooks/useIPhoneMicAlert";
import { IPhoneMicDialog } from "./IPhoneMicDialog";

/**
 * Pre-join consumer of `pendingIPhoneMicAlert`. Renders the dialog when the
 * value is set; passes wired action handlers (switch / use-anyway / dismiss).
 *
 * Mounts inside `MeetingJoinDetails` (lobby route).
 */
export function IPhoneMicDialogContainer() {
  const meetingManager = useMeetingManager();
  const { audioInputDevices, selectAudioInputDevice } = useDevices();

  const { pending, setPending, setUserExplicitlyChose } = usePlatformStore(
    useShallow((s) => ({
      pending: s.pendingIPhoneMicAlert,
      setPending: s.setPendingIPhoneMicAlert,
      setUserExplicitlyChose: s.setUserExplicitlyChoseIPhoneMic,
    })),
  );

  if (!pending) return null;

  const currentLabel = pending.currentDeviceLabel;

  const handleSwitch = async () => {
    // Re-resolve the target against current devices — the originally-stored
    // id may no longer be enumerated by click time.
    const target = suggestedNonContinuityMic(audioInputDevices);
    if (!target) {
      toast.error("No alternative microphone available right now.");
      setPending(null);
      return;
    }

    try {
      await selectAudioInputDevice(target.deviceId);
      // meetingManager may not have started a session yet (we're pre-join),
      // so guard the SDK call. The slice + DevicesProvider state is the
      // source of truth for the lobby device picker.
      try {
        await meetingManager.startAudioInputDevice(target.deviceId);
      } catch {
        // No active session yet — DevicesProvider state is sufficient.
      }
      logIPhoneMicAlertEvent("iphone_mic_alert_switched", {
        surface: "lobby",
        from_device_label: currentLabel,
        to_device_label: target.label,
      });
    } catch {
      toast.error(`Failed to switch to ${target.label}.`);
    } finally {
      setPending(null);
    }
  };

  const handleUseAnyway = () => {
    setUserExplicitlyChose(true);
    logIPhoneMicAlertEvent("iphone_mic_kept", {
      current_device_label: currentLabel,
    });
    setPending(null);
  };

  const handleDismiss = () => {
    logIPhoneMicAlertEvent("iphone_mic_alert_dismissed", {
      surface: "lobby",
      current_device_label: currentLabel,
    });
    setPending(null);
  };

  return (
    <IPhoneMicDialog
      isOpen
      currentDeviceLabel={currentLabel}
      suggestedTargetLabel={pending.suggestedTargetLabel}
      onSwitch={handleSwitch}
      onUseAnyway={handleUseAnyway}
      onDismiss={handleDismiss}
    />
  );
}
